import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";
import { ShoppingCart } from "lucide-react";
import FavoriteButton from "./FavoriteButton";
import { useLanguage } from "./LanguageContext";
import { useDataRefresh } from "./liveUpdates/DataRefreshProvider";
import axios from "axios";
import { api } from "../lib/http";

const API_BASE_URL = "https://shopaholics.ge/api";

/* ================= THEME ================= */
const COLORS = {
  text: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,.08)",
  subtle: "#f7f7f8",
  brand: "#a15a5a",
  brandDark: "#8e4a4a",
  card: "#ffffff",
  shadow: "0 10px 30px rgba(0,0,0,.08)",
};

/* ================= LAYOUT ================= */
const Section = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 16px 28px;

  @media (max-width: 640px) {
    padding-left: 0;
    padding-right: 0;
  }

  h3 {
    margin: 8px 0 14px;
    font-size: 20px;
    font-weight: 900;
    color: ${COLORS.text};
    padding: 0 4px;
  }
`;

const CarouselWrap = styled.div`
  position: relative;
  width: 100%;
`;

/* ჰორიზონტალური ტრეკი snap-ით */
const Track = styled.div`
  display: flex;
  gap: 16px;
  padding: 10px 8px 14px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-padding: 8px;
  -webkit-overflow-scrolling: touch;

  scrollbar-width: thin;
  &::-webkit-scrollbar { height: 8px; }
  &::-webkit-scrollbar-thumb { background: #d9d9dc; border-radius: 8px; }

  @media (max-width: 640px) {
    gap: 12px;
    padding-left: 8px;
    padding-right: 8px;
  }
`;

/* ქაროusele ისრები */
const ArrowBtn = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 42px;
  width: 42px;
  border-radius: 50%;
  border: 1px solid ${COLORS.line};
  background: rgba(255,255,255,.9);
  backdrop-filter: blur(6px);
  display: grid;
  place-items: center;
  box-shadow: ${COLORS.shadow};
  cursor: pointer;
  z-index: 2;
  transition: transform .15s ease, filter .15s ease, opacity .15s ease;

  &:hover { transform: translateY(-50%) scale(1.03); filter: brightness(.98); }
  &:disabled { opacity: .35; cursor: not-allowed; }

  @media (max-width: 640px) {
    height: 36px;
    width: 36px;
  }
`;
const ArrowLeft  = styled(ArrowBtn)` left: 6px; `;
const ArrowRight = styled(ArrowBtn)` right: 6px; `;

/* ================= CARD ================= */
const Card = styled.div`
  scroll-snap-align: start;
  background: ${COLORS.card};
  border: 1px solid ${COLORS.line};
  border-radius: 16px;
  min-width: clamp(200px, 24vw, 260px);
  max-width: 280px;
  padding: 12px;
  position: relative;
  transition: box-shadow .2s ease, transform .2s ease, border-color .2s ease;
  box-sizing: border-box;
  cursor: pointer;

  &:hover {
    box-shadow: ${COLORS.shadow};
    transform: translateY(-2px);
    border-color: rgba(0,0,0,.12);
  }

  /* Overlay მხოლოდ დაკლიკვაზე */
  .hover-options {
    display: ${({ $active }) => ($active ? 'flex' : 'none')};
    position: absolute; inset: 0;
    background: rgba(255,255,255,.6); backdrop-filter: blur(6px);
    align-items: center; justify-content: center; border-radius: 16px;
    z-index: 40; /* ↓ IconBar იქნება ზემოთ */
    pointer-events: ${({ $active }) => ($active ? 'auto' : 'none')};
  }

  .view-btn{
    border: 1px solid ${COLORS.line};
    background:#fff;
    border-radius: 12px;
    height: 40px; padding: 0 14px;
    font-weight: 800; cursor: pointer;
    transition: transform .15s ease, box-shadow .15s ease;
    color: black;
  }
  .view-btn:hover{ transform: translateY(-1px); box-shadow: ${COLORS.shadow}; }

  @media (max-width: 640px) {
    padding: 8px;
    border-radius: 12px;
    min-width: clamp(180px, 48vw, 220px);
  }
`;

const ImgWrap = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 12px;
  background: ${COLORS.subtle};
  display: grid;
  place-items: center;
  overflow: hidden;
  margin-bottom: 10px;
  position: relative;

  @media (max-width: 640px) { margin-bottom: 6px; }
`;

const Image = styled.img`
  width: 80%;
  height: 80%;
  object-fit: contain;     /* პროპორციულად ჩასვავს ჩარჩოში */
  object-position: center; /* სურვილისამებრ: ცენტრში გაასწორებს */
  display: block;          /* inline-gap აღარ იქნება */
  @media (max-width: 640px) { width: 80%; height: 80%; }
`;

const Title = styled.h2`
  font-size: 14px;
  margin: 4px 0 0;
  font-weight: 800;
  color: ${COLORS.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 640px) { font-size: 13px; }
`;

const PriceRow = styled.div`
  margin-top: 8px;
  font-weight: 800;
  color: ${COLORS.text};
  font-size: 13px;

  .old {
    color: #9ca3af;
    text-decoration: line-through;
    margin-right: 6px;
  }

  @media (max-width: 640px) { font-size: 12px; }
`;

const IconBar = styled.div`
  position: absolute;
  left: 10px;
  top: 10px;
  display: flex;
  gap: 8px;
  z-index: 120; /* overlay-ზე მაღლაა, რომ ყოველთვის ჩანდეს/იმუშავოს */

  .icon-btn {
    width: 36px; height: 36px;
    border-radius: 10px;
    border: 1px solid ${COLORS.line};
    background: #fff;
    display: grid; place-items: center;
    cursor: pointer;
    transition: transform .15s ease, box-shadow .15s ease;
  }
  .icon-btn:hover { transform: translateY(-1px); box-shadow: ${COLORS.shadow}; }

  @media (max-width: 640px) {
    .icon-btn { width: 32px; height: 32px; }
  }
`;

/* ================= BADGE ================= */
const blink = keyframes` 0%{opacity:1} 50%{opacity:.2} 100%{opacity:1} `;
const SaleBadge = styled.div`
  position: absolute; top: 10px; right: 10px;
  background: #ef4444; color: #fff; font-weight: 800;
  padding: 6px 10px; border-radius: 999px; font-size: 11px;
  box-shadow: 0 6px 16px rgba(239,68,68,.35);
  animation: ${blink} 1.5s infinite;
  z-index: 100;

  @media (max-width: 640px) { top: 8px; right: 8px; font-size: 10px; padding: 5px 8px; }
`;

/* ================= LINK WRAPPER ================= */
const CardLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: flex;
  flex-direction: column;
`;

/* ================= COMPONENT ================= */
const FavoriteAccessories = ({ addToCart }) => {
    const { version } = useDataRefresh();
  const { t } = useLanguage();
  const [accessories, setAccessories] = useState([]);
  const [activeCard, setActiveCard] = useState(null);
  const trackRef = useRef(null);
  const sectionRef = useRef(null);

  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);


  // პირველივე სურათის ამომღები (string ან {image_url})

  // უფრო ჭკვიანი ამომღები ნებისმიერი ფორმატიდან
const firstImageOf = (arr = []) => {
  if (!Array.isArray(arr) || !arr.length) return null;
  const f = arr[0];
  if (typeof f === "string") return f;
  if (typeof f === "object" && f) {
    return f.image_url || f.url || f.src || f.path || f.photo || null;
  }
  return null;
};

const parseMaybeJsonArray = (v) => {
  try {
    const arr = JSON.parse(v);
    return Array.isArray(arr) ? arr : null;
  } catch { return null; }
};

const firstImageFromVariant = (v) => {
  if (!v) return null;

  // გავრცელებული ველები
  if (Array.isArray(v.images) && v.images.length) {
    const u = firstImageOf(v.images);
    if (u) return u;
  }
  if (Array.isArray(v.imageUrls) && v.imageUrls.length) {
    const u = firstImageOf(v.imageUrls);
    if (u) return u;
  }
  if (Array.isArray(v.image_urls) && v.image_urls.length) {
    const u = firstImageOf(v.image_urls);
    if (u) return u;
  }

  // ერთი ველი ერთ სურათად
  if (v.image_url) return v.image_url;
  if (v.image) return v.image;
  if (v.thumbnail) return v.thumbnail;
  if (v.photo) return v.photo;

  // JSON სტრიქონები
  if (typeof v.images_json === "string") {
    const arr = parseMaybeJsonArray(v.images_json);
    const u = arr && firstImageOf(arr);
    if (u) return u;
  }
  if (typeof v.gallery === "string") {
    const arr = parseMaybeJsonArray(v.gallery);
    const u = arr && firstImageOf(arr);
    if (u) return u;
  }

  // CSV/კომით გამოყოფილი
  if (typeof v.images === "string") {
    const parts = v.images.split(",").map(s => s.trim()).filter(Boolean);
    if (parts[0]) return parts[0];
  }
  if (typeof v.image_urls === "string") {
    const parts = v.image_urls.split(",").map(s => s.trim()).filter(Boolean);
    if (parts[0]) return parts[0];
  }

  return null;
};

// პროდუქტის დონეზე fallback
const firstImageFromProduct = (item = {}) => {
  // შენი არსებული product.imageUrls
  const u1 = firstImageOf(item.imageUrls);
  if (u1) return u1;

  // ზოგჯერ შეიძლება სხვაგან გქონდეს
  if (Array.isArray(item.images) && item.images.length) {
    const u2 = firstImageOf(item.images);
    if (u2) return u2;
  }
  if (Array.isArray(item.photos) && item.photos.length) {
    const u3 = firstImageOf(item.photos);
    if (u3) return u3;
  }
  if (typeof item.images === "string") {
    const parts = item.images.split(",").map(s => s.trim()).filter(Boolean);
    if (parts[0]) return parts[0];
  }
  return null;
};

// საბოლოო ამომღები: ჯერ ვარიაცია, მერე პროდუქტი
const cardImageForItem = (item = {}, selectedVariant = null) => {
  const fromVar = firstImageFromVariant(selectedVariant);
  if (fromVar) return fromVar;
  const fromProd = firstImageFromProduct(item);
  if (fromProd) return fromProd;
  return "/default-image.jpg";
};


// "პირველად დამატებული" სორტი: created_at ASC -> size_sort ASC -> id ASC
const sortVariantsByAdded = (vars=[]) => {
  const parseDate = (d) => (d ? Date.parse(d) || Infinity : Infinity);
  const safeNum = (n) => (n == null ? Infinity : Number(n));
  return [...vars].sort((a,b)=>{
    const ca = parseDate(a.created_at); const cb = parseDate(b.created_at);
    if (ca !== cb) return ca - cb;
    const sa = safeNum(a.size_sort);    const sb = safeNum(b.size_sort);
    if (sa !== sb) return sa - sb;
    const ia = safeNum(a.id || a.variant_id); const ib = safeNum(b.id || b.variant_id);
    return ia - ib;
  });
};

const pickVariantForDisplay = (item = {}) => {
  const variants = Array.isArray(item.variants)
    ? item.variants
    : (Array.isArray(item.accessory_variants) ? item.accessory_variants : []);
  if (!variants.length) return { display: null, purchasable: false };

  const ordered = sortVariantsByAdded(variants);
  const active  = ordered.filter(v => Number(v.is_active ?? 1) === 1);
  const inStock = active.find(v => Number(v.stock ?? v.unit_stock ?? 0) > 0) || null;

  // თუ არცერთი აქვს stock>0 → ვაჩვენოთ პირველი (fallback-first),
  // მაგრამ purchasable=false (ღილაკი არ ჩანდეს)
  const display = inStock || ordered[0] || null;
  const purchasable = !!inStock;

  return { display, purchasable };
};

// აირჩიე პირველი "ხელმისაწვდომი": active && stock>0; თუ ვერ ვიპოვეთ → null
const pickFirstAvailableVariant = (item={}) => {
  const variants = Array.isArray(item.variants)
    ? item.variants
    : (Array.isArray(item.accessory_variants) ? item.accessory_variants : []);
  if (!variants.length) return null;
  const ordered = sortVariantsByAdded(variants);
  const active   = ordered.filter(v => Number(v.is_active ?? 1) === 1);
  const inStock  = active.find(v => Number(v.stock ?? 0) > 0);
  return inStock || null; // შენი მოთხოვნით, თუ ყველა ნულია → არ ვიღებთ არაფერს
};

// კონკრეტული ბარათის სურათი: ჯერ ვარიანტის სურათი, მერე product.imageUrls[0]


// ოდნავ გავაფართოვოთ, რომ სურვილისამებრ გადავცეთ უკვე არჩეული var:
const prepareAccessoryForCart = (item, chosenVar = null) => {
  const v = chosenVar || (() => {
    // უსაფრთხოება – თუ ვერ გადმოგვეწოდა, მაინც მოვძებნოთ in-stock
    const { display, purchasable } = pickVariantForDisplay(item);
    return purchasable ? display : null;
  })();

  if (!v) return null; // არ აქვთ მარაგი → ვერ ვამატებთ

  const price = Number(v.price ?? v.price_p ?? 0);
  const sale  = (v.sale_price != null ? Number(v.sale_price) : Number(v.sale_p ?? 0));
  const useSale = sale > 0 && sale < price;

  return {
    ...item,
    type: "accessory",
    quantity: 1,
    accessory_variant_id: String(v.id ?? v.variant_id ?? v.sku),
    selectedVariant: {
      id: String(v.id ?? v.variant_id ?? v.sku),
      color_ka: v.color_ka ?? v.color ?? null,
      color_en: v.color_en ?? v.color ?? null,
      size_label: v.size_label ?? v.size ?? null,
      images: Array.isArray(v.images) ? v.images : [],
      price,
      stock: Number(v.stock ?? v.unit_stock ?? 0),
      is_active: Number(v.is_active ?? 1),
    },
    full_stock: Number(v.stock ?? v.unit_stock ?? 0),
    price_p: price,
    sale_p: useSale ? sale : null,
    image: cardImageForItem(item, v),
  };
};


  const sortAccessories = (list) =>
    [...list].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const fetchAccessories = async () => {
    const res = await api.get(`${API_BASE_URL}/get_accessories.php`);
    const arr = Array.isArray(res.data) ? res.data : [];
    setAccessories(sortAccessories(arr).slice(0, 10));
  };

 // a) მონაცემების ჩატვირთვა mount-ზე და DB ცვლილებაზე
 useEffect(() => {
   fetchAccessories().catch(console.error);
 }, [version]); // 🔔 version დაემატა

 // b) გარე-კლიკის listener მხოლოდ ერთხელ (არ დაემატოს ყოველი refresh-ზე)
 useEffect(() => {
   const onDocClick = (e) => {
     if (sectionRef.current && !sectionRef.current.contains(e.target)) {
       setActiveCard(null);
     }
   };
   document.addEventListener("click", onDocClick);
   return () => document.removeEventListener("click", onDocClick);
 }, []);

  // ისრების აქტიობის სინქი
  const syncArrows = () => {
    const el = trackRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setAtStart(scrollLeft <= 2);
    setAtEnd(scrollLeft + clientWidth >= scrollWidth - 2);
  };

  useEffect(() => {
    syncArrows();
    const el = trackRef.current;
    if (!el) return;
    const onScroll = () => syncArrows();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", syncArrows);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", syncArrows);
    };
  }, []);

  const getStep = () => {
    const el = trackRef.current;
    if (!el || !el.firstElementChild) return 276;
    const first = el.firstElementChild.getBoundingClientRect();
    const second = el.children?.[1]?.getBoundingClientRect();
    const gap = second ? Math.max(8, Math.round(second.left - first.right)) : 16;
    return Math.round(first.width + gap);
  };

  const scrollLeft = () => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: -getStep(), behavior: "smooth" });
  };

  const scrollRight = () => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: getStep(), behavior: "smooth" });
  };

  const handleCardClick = (id, e) => {
    e.stopPropagation();
    setActiveCard((prev) => (prev === id ? null : id));
  };

  return (
    <Section className="new-products" ref={sectionRef}>
      <h3>{t("newproducts.accessories")}</h3>

      <CarouselWrap>
        <ArrowLeft onClick={scrollLeft} disabled={atStart} aria-label="Scroll left">‹</ArrowLeft>

        <Track ref={trackRef}>
{accessories.map((item) => {
  const variants = Array.isArray(item.variants)
    ? item.variants
    : (Array.isArray(item.accessory_variants) ? item.accessory_variants : []);

  // ★ ახალი არჩევანი
  const { display: selectedVar, purchasable: inStock } = pickVariantForDisplay(item);

  // ფასი – ყოველთვის არჩეული (selectedVar)-ის მიხედვით (თუ საერთოდ არის)
  const basePrice = selectedVar
    ? Number(selectedVar.price ?? selectedVar.price_p ?? 0)
    : Number(item.price_p ?? 0);

  const saleRaw = selectedVar
    ? (selectedVar.sale_price != null
        ? Number(selectedVar.sale_price)
        : Number(selectedVar.sale_p ?? 0))
    : (item.sale_p != null ? Number(item.sale_p) : 0);

  const hasSale = selectedVar ? (saleRaw > 0 && saleRaw < basePrice) : false;

  // ბეჯი – თუ რომელიმე ვარიანტი მაინც აქცციაშია
  const anyVariantOnSale = Array.isArray(variants) && variants.some(v => {
    const r = Number(v.price ?? v.price_p ?? 0);
    const s = Number(v.sale_price ?? v.sale_p ?? 0);
    return r > 0 && s > 0 && s < r;
  });

  const isActive = activeCard === item.product_id;

  return (
    <Card
      key={item.product_id}
      onClick={(e) => handleCardClick(item.product_id, e)}
      $active={isActive}
    >
      <div className="hover-options" onClick={(e) => { e.stopPropagation(); setActiveCard(null); }}>
        <Link to={`/product/${item.product_id}`} onClick={(e) => e.stopPropagation()}>
          <button className="view-btn">{t("categorypage.viewmore") || "სრულად ნახვა"}</button>
        </Link>
      </div>

      {(hasSale || anyVariantOnSale) && <SaleBadge>{t("newproducts.sale")}</SaleBadge>}

      <CardLink to="#" onClick={(e) => e.preventDefault()}>
<ImgWrap>
  <Image
    key={(selectedVar && (selectedVar.id || selectedVar.variant_id || selectedVar.sku || 'var')) + ':' + (cardImageForItem(item, selectedVar) || 'noimg')}
    src={cardImageForItem(item, selectedVar)}
    alt={item.name}
  />
</ImgWrap>

        <Title title={item.name}>{item.name}</Title>

        <PriceRow>
          {hasSale ? (
            <>
              <span className="old">{basePrice} {t("newproducts.currency")}</span>
              <span>{saleRaw} {t("newproducts.currency")}</span>
            </>
          ) : (
            <span>{basePrice} {t("newproducts.currency")}</span>
          )}
        </PriceRow>
      </CardLink>

      <IconBar>
        {inStock && (
          <button
            className="icon-btn"
            onClick={(e) => {
              e.stopPropagation();
              const prepared = prepareAccessoryForCart(item, selectedVar); // ← პირდაპირ არჩეულით
              if (!prepared) return;
              addToCart(prepared);
            }}
            aria-label={t("categorypage.addtocart") || "Add to cart"}
            title={t("categorypage.addtocart") || "Add to cart"}
          >
            <ShoppingCart size={18} />
          </button>
        )}

        <div className="icon-btn" onClick={(e) => e.stopPropagation()}>
          <FavoriteButton
            productId={item.product_id}
            initialIsFavorite={item.is_favorite}
          />
        </div>
      </IconBar>  
    </Card>
  );
})}

        </Track>

        <ArrowRight onClick={scrollRight} disabled={atEnd} aria-label="Scroll right">›</ArrowRight>
      </CarouselWrap>

      <div className="all-perfumes-cont" style={{ textAlign: "center", marginTop: 12 }}>
        <Link
          to="/products?category=accessories"
          className="all-perfumes"
          style={{
            display: "inline-block",
            padding: "10px 16px",
            borderRadius: 12,
            background: COLORS.text,
            color: "#fff",
            textDecoration: "none",
            fontWeight: 800,
            border: `1px solid ${COLORS.line}`,
            boxShadow: COLORS.shadow,
          }}
        >
          {t("newproducts.viewall")}
        </Link>
      </div>
    </Section>
  );
};

export default FavoriteAccessories;
